export interface RetryConfig {
  initialDelayMs: number;
  multiplier: number;
  maxDelayMs: number;
  jitter: 'full' | 'none';
  maxRetries: number | null; // null = retry forever
  stableAfterMs: number; // connection must live this long before backoff resets
  flapThreshold: number; // rapid open→drop cycles before cooldown
  cooldownMinMs: number;
  cooldownMaxMs: number;
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  cooldownMaxMs: 60_000,
  cooldownMinMs: 30_000,
  flapThreshold: 3,
  initialDelayMs: 300,
  jitter: 'full',
  maxDelayMs: 10_000,
  maxRetries: null,
  multiplier: 2,
  stableAfterMs: 30_000,
};

export interface ReconnectInfo {
  attempt: number;
  consecutiveFailures: number;
  downtimeMs: number;
  flapping: boolean;
}

/**
 * Ceiling for a server-supplied retry-after hint. A buggy (or hostile) server must not be able
 * to park clients for hours; anything beyond this is clamped down to it.
 */
export const RETRY_AFTER_MAX_MS = 300_000;

export const getRetryDelayMs = (
  attempt: number,
  config: RetryConfig,
  retryAfterHintMs?: number,
  random: () => number = Math.random,
): number => {
  // Non-finite hints (NaN from Number() on a malformed close reason, Infinity) are treated as
  // absent: NaN survives Math.min/Math.max and the spread, then setTimeout(cb, NaN) fires
  // immediately — every client parsing the same bad reason would reconnect in lockstep.
  if (retryAfterHintMs !== undefined && Number.isFinite(retryAfterHintMs)) {
    // The hint still overrides the ladder — that is what it is for — but it is sanitized and,
    // under full jitter, spread: a uniform hint sent to every client resynchronizes the whole
    // fleet onto the same instant, and hint <= 0 would otherwise mean an instant reconnect storm.
    const hint = Math.min(Math.max(retryAfterHintMs, 0), RETRY_AFTER_MAX_MS);
    return config.jitter === 'full' ? hint + random() * Math.max(hint * 0.25, 1000) : hint;
  }
  if (attempt <= 0) {
    return 0;
  }
  const ceiling = Math.min(config.maxDelayMs, config.initialDelayMs * Math.pow(config.multiplier, attempt - 1));
  return config.jitter === 'full' ? random() * ceiling : ceiling;
};

export const getCooldownMs = (config: RetryConfig, random: () => number = Math.random): number =>
  config.cooldownMinMs + random() * (config.cooldownMaxMs - config.cooldownMinMs);

export class RetryTracker {
  private attempt = 0;
  private consecutiveFailures = 0;
  private flapCount = 0;
  private outageStart?: number;
  private lastOpenTime?: number;
  private flappedRecently = false;

  constructor(
    private config: RetryConfig,
    private now: () => number = Date.now,
  ) {}

  /** Transport handshake completed. Does NOT reset backoff — stability is judged at the next drop. */
  public onOpened = (): void => {
    this.lastOpenTime = this.now();
    this.outageStart = undefined;
  };

  /** Transport dropped (unclean close or handshake failure). */
  public onDropped = (): void => {
    if (this.lastOpenTime !== undefined) {
      const lifetime = this.now() - this.lastOpenTime;
      if (lifetime >= this.config.stableAfterMs) {
        // Stability window passed: this is a fresh outage, restart the ladder.
        this.attempt = 0;
        this.consecutiveFailures = 0;
        this.flapCount = 0;
        this.flappedRecently = false;
      } else {
        // Opened but died early: a flap. Ladder keeps climbing.
        this.flapCount += 1;
      }
      this.lastOpenTime = undefined;
    }
    if (this.outageStart === undefined) {
      this.outageStart = this.now();
    }
    this.consecutiveFailures += 1;
  };

  /** Returns the attempt index to feed getRetryDelayMs, then increments. */
  public nextAttempt = (): number => this.attempt++;

  public isExhausted = (): boolean => this.config.maxRetries !== null && this.attempt > this.config.maxRetries;

  /** True once per flap burst, when flapCount reaches flapThreshold. */
  public shouldCooldown = (): boolean => {
    if (this.flapCount >= this.config.flapThreshold) {
      this.flapCount = 0;
      this.flappedRecently = true;
      return true;
    }
    return false;
  };

  public info = (): ReconnectInfo => ({
    attempt: Math.max(0, this.attempt - 1),
    consecutiveFailures: this.consecutiveFailures,
    downtimeMs: this.outageStart === undefined ? 0 : this.now() - this.outageStart,
    flapping: this.flapCount > 0 || this.flappedRecently,
  });

  public reset = (): void => {
    this.attempt = 0;
    this.consecutiveFailures = 0;
    this.flapCount = 0;
    this.flappedRecently = false;
    this.outageStart = undefined;
    this.lastOpenTime = undefined;
  };
}
